import React, { useState, FC, ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { Theme, lighten } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import AppBar from '@mui/material/AppBar';
// Components
import { clsx } from 'clsx';
import { AxiosError } from 'axios';
import MobileAppBar from './mobileAppBar';
import DesktopAppBar from './desktopAppBar';
import menuContent, { NavigationHeaderInfos } from './menuContent';
import SvgIcon from '../icons/SvgIcon';
import { IconCommon } from '../../resources/images';
import LoaderFullPage from '../loaderFullPage';
// Modules
import { getLogout, getUndress } from '../../core/services/id';
import { selectors as userSelectors } from '../../core/modules/user';
import { actions as selfServiceActions } from '../../core/modules/selfService';
import { actions as modalActions, ModalType, ActionOpenModal } from '../../core/modules/modals';
// Queries
import { useHasUnsignedContract } from '../../core/services/contract';
import { useResumeSelfService } from '../../core/services/selfService';
// Resources
import configs from '../../config/index';
import { paths } from '../../navigation/routes/paths';
import { stringsRes } from '../../resources/strings';
import { getFullnameMaxed, isB2B, isUserAdmin, isUserFullB2C, isUserImpersonating } from '../../utils/user';
import { KEY_WV_STATUS_BAR, asyncStorageApi, getTopBarHeight, getUser } from '../../localStorageAPI';
import { ModalSize } from '../modals/createModalHOC';
import { DEFAULT_TOP_BAR_HEIGHT, NavTopBarHeight } from '../../utils/constants';
import { useBreakpoints, WebLocation } from '../../customHooks/useBreakpoints';
import featureFlag from '../../config/featureFlag';
import { GetResumeSelfServiceOutput, HasContractUnsignedOutput } from '../../core/apiTypes';
import { isWebView } from '../../utils/webview';
import { noTopBar } from '../../utils/page';

const leftHeaderData: NavigationHeaderInfos[] = [
    // {
    //     label: stringsRes.header.makeReservation,
    //     href: paths.reservations,
    //     icon: <DriveEtaIcon />,
    // },
];

interface ResponsiveAppBarProps {
    lang: string;
    shouldRefresh?: boolean;
    openModal: ActionOpenModal;
    resumeSelfService: (data: GetResumeSelfServiceOutput) => void;
}

const ResponsiveAppBar: FC<ResponsiveAppBarProps> = (props): ReactElement => {
    const classes = useStyles({
        statusBarHeight: getTopBarHeight() || DEFAULT_TOP_BAR_HEIGHT,
    });
    const { aboveAll, isMobileSize } = useBreakpoints(WebLocation.FLOW);
    const { pathname } = useLocation();
    const currentUser = getUser();

    const { lang, shouldRefresh = false, openModal, resumeSelfService } = props;

    const [drawerOpen, toggleDrawer] = useState<boolean>(false);

    const [user, setUser] = useState(getUser());

    const isAdmin = isUserAdmin(user);

    // Handle check and fetch resume for Self Service
    const { refetch } = useResumeSelfService<GetResumeSelfServiceOutput>({
        enabled: false,
        onSuccess: (data: GetResumeSelfServiceOutput) => {
            console.log('GetResumeSelfServiceOutput', data);
            resumeSelfService(data);
        },
    });

    const { data: unsignedContract } = useHasUnsignedContract<HasContractUnsignedOutput>({
        enabled: featureFlag.selfService.active && isUserFullB2C(),
        onSuccess: (data: HasContractUnsignedOutput) => {
            console.log('useHasUnsignedContract', data);
            refetch();
        },
        onError: (error: AxiosError) => {
            console.log(error.response?.status, error.response?.data);
        },
    });

    React.useEffect(() => {}, [lang]);

    React.useEffect(() => {
        setUser(getUser());
    }, [pathname]);

    React.useEffect(() => {
        if (currentUser.id !== user.id) {
            // TODO: Remove reload and handle this more properly
            // Would be better with no reload but need to make sure the rest of the view is updated too (home or others ?)
            // the best would be to have a loader when the token is different than before only
            // setUser(getUser());
            window.location.reload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    const rightHeaderData: NavigationHeaderInfos[] = [
        {
            label: stringsRes.header.bookRide,
            href: paths.home,
        },
        {
            label: !aboveAll ? user?.firstname : (getFullnameMaxed(user) ?? stringsRes.header.profil),
            href: paths.profile,
            menuItems: menuContent.menuBuilder({}),
        },
    ];

    // Add Help menu for B2B only
    if (isB2B()) {
        rightHeaderData.splice(1, 0, {
            label: stringsRes.header.support,
            href: paths.support,
        });
    }

    // Add Admin menu when needed
    if (isAdmin) {
        rightHeaderData.splice(1, 0, {
            label: stringsRes.header.admin,
            href: paths.adminGroups,
            menuItems: menuContent.menuAdminBuilder({ isAdmin: false }),
        });
    }

    // Add SelfService menu if flag is activated
    // if (featureFlag.selfService.active && isUserFullB2C()) {
    if ((featureFlag.selfService.active || unsignedContract?.contract_uuid) && isUserFullB2C()) {
        rightHeaderData.splice(0, 0, {
            label: unsignedContract?.contract_uuid
                ? stringsRes.header.selfServiceContinue
                : stringsRes.header.selfService,
            href: paths.selfServiceFormula,
            icon: <SvgIcon icon={IconCommon.Briefcase} size="mediumSmall" stroke="unset" />,
            backgroundColor: 'blue',
        });
    }

    // Add ID menu for dev envs
    if (configs.envConfig.currentEnv.isDev) {
        rightHeaderData.splice(0, 0, {
            label: stringsRes.header.id,
            href: paths.hb,
        });
    }

    const onOpenCloseDrawer = (): void => {
        toggleDrawer((current) => !current);
    };

    const onLogout = (): void => {
        openModal(ModalType.CONFIRMATION, {
            title: stringsRes.profile.logout,
            description: stringsRes.profile.logoutMessage,
            modalSize: ModalSize.md,
            onConfirmAction: () => {
                if (isUserImpersonating()) {
                    getUndress();
                } else {
                    getLogout();
                }
            },
        });
    };

    const onFlow = noTopBar();
    const isWebview = isWebView();
    const shouldHandleStatusBar = asyncStorageApi.restoreDataNoLink(KEY_WV_STATUS_BAR);

    if (pathname === paths.root || pathname.endsWith(paths.rootWebView)) {
        return <LoaderFullPage msg={stringsRes.common.loading} backgroundColor="lightblue" withIcon />;
    }

    return (
        <>
            {isWebview && !onFlow && shouldHandleStatusBar && <div className={classes.statusBar} />}
            {/* ne pas retirer (useWebviewHeader) */}
            <div id="header-wrap" className={clsx((!onFlow || !isMobileSize) && classes.headerWrap)}>
                <AppBar
                    className={clsx(
                        (!onFlow || !isMobileSize) && classes.header,
                        isMobileSize && classes.headerMobile,
                        !drawerOpen && classes.backgroundResponsive,
                        user?.puppeteer && classes.impersonate,
                        isWebview && !onFlow && shouldHandleStatusBar && classes.statusBarMargin,
                    )}
                    position={!aboveAll ? 'fixed' : 'static'}
                    id="header" // ne pas retirer (useWebviewHeader)
                >
                    {isMobileSize ? (
                        <MobileAppBar
                            shouldRefresh={shouldRefresh}
                            onOpenDrawer={onOpenCloseDrawer}
                            onCloseDrawer={onOpenCloseDrawer}
                            isDrawerOpen={drawerOpen}
                            onLogout={onLogout}
                        />
                    ) : (
                        <DesktopAppBar
                            shouldRefresh={shouldRefresh}
                            onLogout={onLogout}
                            leftMenuEntries={leftHeaderData}
                            rightMenuEntries={rightHeaderData}
                        />
                    )}
                </AppBar>
            </div>
        </>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        statusBar: {
            position: 'fixed',
            top: 0,
            width: '100%',
            height: (props: { statusBarHeight: number }) => props.statusBarHeight,
            zIndex: 2,
            background: theme.palette.background.default,
        },
        statusBarMargin: {
            marginTop: (props: { statusBarHeight: number }) => props.statusBarHeight,
        },
        headerWrap: {
            position: 'fixed',
            top: 0,
            height: 52,
            width: '100%',
            zIndex: 2,
        },
        header: {
            width: '100%',
            display: 'flex',
            flex: 1,
            zIndex: 10,
            backgroundColor: theme.palette.colors.background.appBar,
            color: theme.palette.primary.main,
            paddingRight: '25px',
            paddingLeft: '25px',
            boxShadow: 'none',
            borderBottom: `1px solid ${theme.palette.colors.background.menuSeparator}`,
            position: 'absolute',
            top: 0,
            height: NavTopBarHeight,
        },
        headerMobile: {
            padding: 0,
            paddingRight: 8,
            // background: 'transparent',
            // borderBottom: 'unset!important',
        },
        backgroundResponsive: {
            background: theme.palette.colors.background.appBar,
            zIndex: 3,
        },
        impersonate: {
            backgroundColor: `${lighten(theme.palette.colors.text.warning, 0.4)} !important`,
        },
    }),
);

const mapStateToProps = (state) => ({
    lang: userSelectors.language(state),
});

const mapDispatchToProps = {
    openModal: modalActions.openModal,
    resumeSelfService: selfServiceActions.resume,
};

export default connect(mapStateToProps, mapDispatchToProps)(ResponsiveAppBar);
